/**
 * Locales, direction, and turning a localized document into one locale's strings.
 *
 * `pick` is on the scoring path — a verdict's message is resolved through it —
 * so it must be pure and deterministic across engines (invariant 7). It uses no
 * `Intl`, no `localeCompare` and no locale-sensitive comparison of any kind.
 */

/** Lowercase BCP-47-shaped tag: `he`, `en`, `ar`, `he-il`. */
export type Locale = string;

export type Dir = 'rtl' | 'ltr';

/** One string per locale, as it appears on disk. */
export type LocalizedText = { readonly [locale: string]: string };

/**
 * Base languages written right-to-left. Hebrew is the majority of CET content,
 * so this is the common case rather than the exception.
 */
const RTL_BASE_LANGUAGES: ReadonlySet<string> = new Set([
  'ar',
  'arc',
  'dv',
  'fa',
  'he',
  'ku',
  'ps',
  'sd',
  'ug',
  'ur',
  'yi',
]);

function baseLanguage(locale: Locale): string {
  const dash = locale.indexOf('-');
  return dash === -1 ? locale : locale.slice(0, dash);
}

export function dirFor(locale: Locale): Dir {
  return RTL_BASE_LANGUAGES.has(baseLanguage(locale)) ? 'rtl' : 'ltr';
}

/**
 * Resolve a localized value for one locale.
 *
 * Exact tag, then base language (`he-il` → `he`), then — as a last resort — the
 * alphabetically first locale present. That last step is alphabetical rather
 * than "the first key" on purpose: object key order follows how the JSON
 * happened to be written, and the browser and the server must agree byte for
 * byte on a resolved message.
 *
 * Returns `''` for an empty document rather than throwing. A missing string is
 * a validation finding, not a runtime failure — an asset mid-activity should
 * render an empty prompt, not crash.
 */
export function pick(text: LocalizedText, locale: Locale): string {
  const exact = text[locale];
  if (exact !== undefined) return exact;

  const base = baseLanguage(locale);
  if (base !== locale) {
    const viaBase = text[base];
    if (viaBase !== undefined) return viaBase;
  }

  const firstAlphabetically = Object.keys(text).sort()[0];
  if (firstAlphabetically === undefined) return '';
  return text[firstAlphabetically] ?? '';
}
